import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    TextField,
    Typography,
    Alert,
    CircularProgress,
    Paper,
    Container,
} from '@mui/material';
import { LocalHospital, ArrowBack } from '@mui/icons-material';
import { authService } from '../../services/authService';

export const ForgotPasswordPage = () => {
    const { t } = useTranslation();

    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await authService.forgotPassword(email);
            setSuccess(true);
        } catch (err: any) {
            // Still show success to prevent email enumeration
            setSuccess(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <LocalHospital sx={{ fontSize: 48, color: 'primary.main' }} />
                    </Box>

                    <Typography variant="h5" component="h1" gutterBottom textAlign="center">
                        {t('auth.forgotPassword.title')}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                        {t('auth.forgotPassword.subtitle')}
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success ? (
                        <Box>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {t('auth.forgotPassword.success')}
                            </Alert>
                            <Button
                                component={Link}
                                to="/login"
                                fullWidth
                                variant="outlined"
                                startIcon={<ArrowBack />}
                            >
                                {t('auth.forgotPassword.backToLogin')}
                            </Button>
                        </Box>
                    ) : (
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label={t('auth.forgotPassword.email')}
                                name="email"
                                autoComplete="email"
                                autoFocus
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loading}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                disabled={loading || !email}
                            >
                                {loading ? <CircularProgress size={24} /> : t('auth.forgotPassword.submit')}
                            </Button>

                            <Box sx={{ textAlign: 'center' }}>
                                <Link
                                    to="/login"
                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                >
                                    <Typography variant="body2" color="primary">
                                        {t('auth.forgotPassword.backToLogin')}
                                    </Typography>
                                </Link>
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};
